import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { SoulConfig, SoulSection } from '../../types/soul.js';
import type { Agent } from '../../types/agent.js';
import * as soulApi from '../../services/soul-api.js';
import * as agentApi from '../../services/agent-api.js';

type ViewMode = 'structured' | 'markdown';

function parseJsonField<T>(value: string, fallback: T): T {
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

@customElement('cp-soul-editor')
export class CpSoulEditor extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) agentId: number | null = null;

  @state() private _loading = true;
  @state() private _saving = false;
  @state() private _error = '';
  @state() private _successMessage = '';
  @state() private _viewMode: ViewMode = 'structured';

  // Structured fields
  @state() private _agentName = '';
  @state() private _personalityType = '';
  @state() private _communicationStyle = '';
  @state() private _coreValues: string[] = [];
  @state() private _boundaries = '';
  @state() private _proactivityLevel = '';
  @state() private _goalWorkGuidelines = '';

  // Markdown fields
  @state() private _markdownText = '';
  @state() private _wordCount = 0;

  private _lastAgentId: number | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this._loadSoulConfig();
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('agentId') && this.agentId !== this._lastAgentId) {
      this._lastAgentId = this.agentId;
      void this._loadSoulConfig();
    }
  }

  private async _loadSoulConfig(): Promise<void> {
    this._loading = true;
    this._error = '';
    try {
      if (this.agentId != null) {
        const agent = await agentApi.getAgent(this.agentId);
        this._applyAgentConfig(agent);
      } else {
        const config = await soulApi.getSoulConfig();
        this._applyConfig(config);
      }
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Fehler beim Laden der Soul-Konfiguration';
    } finally {
      this._loading = false;
    }
  }

  private _applyConfig(config: SoulConfig): void {
    this._agentName = config.agent_name;
    this._personalityType = config.personality_type;
    this._communicationStyle = config.communication_style?.content ?? '';
    this._coreValues = [...(config.core_values ?? [])];
    this._boundaries = config.boundaries?.content ?? '';
    this._proactivityLevel = config.proactivity_level?.content ?? '';
    this._goalWorkGuidelines = config.goal_work_guidelines?.content ?? '';
    this._markdownText = config.full_markdown ?? '';
    this._wordCount = config.word_count ?? 0;
  }

  private _applyAgentConfig(agent: Agent): void {
    this._agentName = agent.name;
    this._personalityType = agent.personality_type;
    const commStyle = parseJsonField<{ content?: string }>(agent.communication_style, {});
    this._communicationStyle = commStyle.content ?? '';
    this._coreValues = parseJsonField<string[]>(agent.core_values, []);
    const bounds = parseJsonField<{ content?: string }>(agent.boundaries, {});
    this._boundaries = bounds.content ?? '';
    const proact = parseJsonField<{ content?: string }>(agent.proactivity_level, {});
    this._proactivityLevel = proact.content ?? '';
    const goalWork = parseJsonField<{ content?: string }>(agent.goal_work_guidelines, {});
    this._goalWorkGuidelines = goalWork.content ?? '';
    this._markdownText = agent.full_markdown ?? '';
    this._wordCount = this._countWords(this._markdownText);
  }

  private _onAgentNameInput(e: Event): void {
    this._agentName = (e.target as HTMLInputElement).value;
    this._successMessage = '';
  }

  private _onPersonalityTypeInput(e: Event): void {
    this._personalityType = (e.target as HTMLInputElement).value;
    this._successMessage = '';
  }

  private _onCommunicationStyleInput(e: Event): void {
    this._communicationStyle = (e.target as HTMLTextAreaElement).value;
    this._successMessage = '';
  }

  private _onBoundariesInput(e: Event): void {
    this._boundaries = (e.target as HTMLTextAreaElement).value;
    this._successMessage = '';
  }

  private _onProactivityLevelInput(e: Event): void {
    this._proactivityLevel = (e.target as HTMLTextAreaElement).value;
    this._successMessage = '';
  }

  private _onGoalWorkGuidelinesInput(e: Event): void {
    this._goalWorkGuidelines = (e.target as HTMLTextAreaElement).value;
    this._successMessage = '';
  }

  private _onCoreValueInput(e: Event, index: number): void {
    const newValues = [...this._coreValues];
    newValues[index] = (e.target as HTMLInputElement).value;
    this._coreValues = newValues;
    this._successMessage = '';
  }

  private _addCoreValue(): void {
    this._coreValues = [...this._coreValues, ''];
    this._successMessage = '';
    this.updateComplete.then(() => {
      const inputs = this.querySelectorAll<HTMLInputElement>('.cp-soul-editor__value-input');
      inputs[inputs.length - 1]?.focus();
    });
  }

  private _removeCoreValue(index: number): void {
    this._coreValues = this._coreValues.filter((_, i) => i !== index);
    this._successMessage = '';
  }

  private _onMarkdownInput(e: Event): void {
    this._markdownText = (e.target as HTMLTextAreaElement).value;
    this._wordCount = this._countWords(this._markdownText);
    this._successMessage = '';
  }

  private _countWords(text: string): number {
    return text.trim().split(/\s+/).filter(Boolean).length;
  }

  private async _save(): Promise<void> {
    if (this._saving) return;

    this._saving = true;
    this._error = '';
    this._successMessage = '';

    try {
      if (this.agentId != null) {
        await this._saveAgentSoul();
      } else {
        await this._saveSoulConfig();
      }
      this._successMessage = 'Soul-Konfiguration gespeichert';
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Fehler beim Speichern';
    } finally {
      this._saving = false;
    }
  }

  private async _saveSoulConfig(): Promise<void> {
    let config: SoulConfig;

    if (this._viewMode === 'markdown') {
      config = await soulApi.updateSoulMarkdown(this._markdownText);
    } else {
      const section = (content: string): SoulSection => ({ enabled: true, content });
      config = await soulApi.updateSoulConfig({
        agent_name: this._agentName,
        personality_type: this._personalityType,
        communication_style: section(this._communicationStyle),
        core_values: this._coreValues.filter(v => v.trim()),
        boundaries: section(this._boundaries),
        proactivity_level: section(this._proactivityLevel),
        goal_work_guidelines: section(this._goalWorkGuidelines),
      });
    }

    this._applyConfig(config);
  }

  private async _saveAgentSoul(): Promise<void> {
    if (this.agentId == null) return;

    const agent = await agentApi.updateAgent(this.agentId, {
      name: this._agentName,
      personality_type: this._personalityType,
      full_markdown: this._markdownText,
    });
    this._applyAgentConfig(agent);
  }

  private _renderStructured() {
    return html`
      <div class="cp-soul-editor__form">
        <div class="cp-soul-editor__field">
          <label class="cp-soul-editor__label" for="soul-agent-name">Agent-Name</label>
          <input
            id="soul-agent-name"
            class="cp-soul-editor__input"
            type="text"
            .value=${this._agentName}
            @input=${this._onAgentNameInput}
          />
        </div>

        <div class="cp-soul-editor__field">
          <label class="cp-soul-editor__label" for="soul-personality">Persoenlichkeits-Typ</label>
          <input
            id="soul-personality"
            class="cp-soul-editor__input"
            type="text"
            .value=${this._personalityType}
            @input=${this._onPersonalityTypeInput}
          />
        </div>

        <div class="cp-soul-editor__field">
          <label class="cp-soul-editor__label" for="soul-comm-style">Kommunikationsstil</label>
          <textarea
            id="soul-comm-style"
            class="cp-soul-editor__textarea"
            rows="4"
            .value=${this._communicationStyle}
            @input=${this._onCommunicationStyleInput}
          ></textarea>
        </div>

        <div class="cp-soul-editor__field">
          <label class="cp-soul-editor__label">Core Values</label>
          <div class="cp-soul-editor__list">
            ${this._coreValues.map((value, index) => html`
              <div class="cp-soul-editor__list-item">
                <input
                  class="cp-soul-editor__value-input"
                  type="text"
                  .value=${value}
                  @input=${(e: Event) => this._onCoreValueInput(e, index)}
                />
                <button
                  class="cp-soul-editor__btn cp-soul-editor__btn--remove"
                  @click=${() => this._removeCoreValue(index)}
                  title="Entfernen"
                >&times;</button>
              </div>
            `)}
            <button
              class="cp-soul-editor__btn cp-soul-editor__btn--add"
              @click=${this._addCoreValue}
            >+ Value hinzufuegen</button>
          </div>
        </div>

        <div class="cp-soul-editor__field">
          <label class="cp-soul-editor__label" for="soul-boundaries">Boundaries</label>
          <textarea
            id="soul-boundaries"
            class="cp-soul-editor__textarea"
            rows="4"
            .value=${this._boundaries}
            @input=${this._onBoundariesInput}
          ></textarea>
        </div>

        <div class="cp-soul-editor__field">
          <label class="cp-soul-editor__label" for="soul-proactivity">Proaktivitaets-Level</label>
          <textarea
            id="soul-proactivity"
            class="cp-soul-editor__textarea"
            rows="4"
            .value=${this._proactivityLevel}
            @input=${this._onProactivityLevelInput}
          ></textarea>
        </div>

        <div class="cp-soul-editor__field">
          <label class="cp-soul-editor__label" for="soul-goal-guidelines">Zielarbeit-Richtlinien</label>
          <textarea
            id="soul-goal-guidelines"
            class="cp-soul-editor__textarea"
            rows="4"
            .value=${this._goalWorkGuidelines}
            @input=${this._onGoalWorkGuidelinesInput}
          ></textarea>
        </div>
      </div>
    `;
  }

  private _renderMarkdown() {
    return html`
      <div class="cp-soul-editor__markdown">
        <textarea
          class="cp-soul-editor__textarea cp-soul-editor__textarea--markdown"
          rows="20"
          .value=${this._markdownText}
          @input=${this._onMarkdownInput}
          placeholder="Soul als Markdown schreiben..."
        ></textarea>
      </div>
    `;
  }

  private _renderWordCount() {
    const count = (this.agentId != null || this._viewMode === 'markdown')
      ? this._wordCount
      : this._countWords([
          this._agentName,
          this._personalityType,
          this._communicationStyle,
          ...this._coreValues,
          this._boundaries,
          this._proactivityLevel,
          this._goalWorkGuidelines,
        ].join(' '));

    const isOver = count > 2000;

    return html`
      <div class="cp-soul-editor__word-count ${isOver ? 'cp-soul-editor__word-count--warning' : ''}">
        ${count} Woerter${isOver ? html` <span class="cp-soul-editor__word-count-warning">Soul ueberschreitet das empfohlene Token-Budget (2000 Woerter)</span>` : nothing}
      </div>
    `;
  }

  private _renderCompanyAgent() {
    return html`
      <div class="cp-soul-editor__form">
        <div class="cp-soul-editor__field">
          <label class="cp-soul-editor__label" for="soul-agent-name">Agent-Name</label>
          <input
            id="soul-agent-name"
            class="cp-soul-editor__input"
            type="text"
            .value=${this._agentName}
            @input=${this._onAgentNameInput}
          />
        </div>

        <div class="cp-soul-editor__field">
          <label class="cp-soul-editor__label" for="soul-personality">Persoenlichkeits-Typ</label>
          <input
            id="soul-personality"
            class="cp-soul-editor__input"
            type="text"
            .value=${this._personalityType}
            @input=${this._onPersonalityTypeInput}
          />
        </div>

        <div class="cp-soul-editor__field">
          <label class="cp-soul-editor__label" for="soul-system-prompt">System Prompt</label>
          <textarea
            id="soul-system-prompt"
            class="cp-soul-editor__textarea cp-soul-editor__textarea--markdown"
            rows="20"
            .value=${this._markdownText}
            @input=${this._onMarkdownInput}
            placeholder="System Prompt als Markdown schreiben..."
          ></textarea>
        </div>
      </div>
    `;
  }

  render() {
    const isCompanyAgent = this.agentId != null;

    return html`
      <div class="cp-soul-editor">
        <div class="cp-soul-editor__header">
          <h3 class="cp-soul-editor__title">${isCompanyAgent ? 'Agent Prompt' : 'Agent Soul'}</h3>
          ${!isCompanyAgent ? html`
            <div class="cp-soul-editor__toggle">
              <button
                class="cp-soul-editor__toggle-btn ${this._viewMode === 'structured' ? 'cp-soul-editor__toggle-btn--active' : ''}"
                @click=${() => { this._viewMode = 'structured'; this._successMessage = ''; }}
              >Strukturiert</button>
              <button
                class="cp-soul-editor__toggle-btn ${this._viewMode === 'markdown' ? 'cp-soul-editor__toggle-btn--active' : ''}"
                @click=${() => { this._viewMode = 'markdown'; this._successMessage = ''; }}
              >Markdown</button>
            </div>
          ` : nothing}
        </div>

        ${this._error ? html`
          <div class="cp-soul-editor__error">${this._error}</div>
        ` : nothing}

        ${this._successMessage ? html`
          <div class="cp-soul-editor__success">${this._successMessage}</div>
        ` : nothing}

        ${this._loading ? html`
          <p class="cp-soul-editor__loading">Soul-Konfiguration wird geladen...</p>
        ` : html`
          ${isCompanyAgent
            ? this._renderCompanyAgent()
            : this._viewMode === 'structured' ? this._renderStructured() : this._renderMarkdown()}

          ${this._renderWordCount()}

          <button
            class="cp-soul-editor__btn cp-soul-editor__btn--save"
            @click=${this._save}
            ?disabled=${this._saving}
          >${this._saving ? 'Speichert...' : 'Speichern'}</button>
        `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-soul-editor': CpSoulEditor;
  }
}
